import React, { useMemo } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Typography, Box, IconButton } from '@mui/material';
import CardHeader from '@mui/material/CardHeader';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { WidgetEditControls } from './WidgetEditControls';
import { WidgetConfig } from './widget';
import { useWidgetData } from './useWidgetData';

interface ChartWidgetProps {
  widget: WidgetConfig;
  onEdit: (widget: WidgetConfig) => void;
  onRemove: (widgetId: string) => void;
  onSave?: (widget: WidgetConfig) => void;
  hasUnsavedChanges?: boolean;
  isEditing: boolean;
}

export const ChartWidget = ({
  widget,
  onEdit,
  onRemove,
  onSave,
  hasUnsavedChanges,
  isEditing,
}: ChartWidgetProps) => {
  const { data, isLoading, error, refetch } = useWidgetData(widget);

  const { chartType } = widget;

  const chartData = useMemo(() => {
    if (widget.data) {
      return widget.data;
    }

    if (data?.data?.Results && Array.isArray(data.data.Results)) {
      return data.data.Results;
    }

    if (data?.data && Array.isArray(data.data)) {
      return data.data;
    }

    return [];
  }, [widget.data, data]);

  const pieData = useMemo(() => {
    if (chartType !== 'pie' || !chartData || chartData.length === 0) return [];

    const categoryKey = widget.chartConfiguration.xAxis.columnName || 'name';
    const valueKey = widget.chartConfiguration.yAxis?.columnName;
    const valueMap = new Map();

    chartData.forEach((item: any) => {
      let category;

      if (
        item.telemetry &&
        typeof item.telemetry === 'object' &&
        item.telemetry[categoryKey] !== undefined
      ) {
        category = item.telemetry[categoryKey];
      } else {
        category = item[categoryKey];
      }

      if (category !== null && category !== undefined && category !== '') {
        const currentValue = valueMap.get(category) || 0;
        let valueToAdd = 1;

        if (valueKey) {
          let specificValue;

          if (
            item.telemetry &&
            typeof item.telemetry === 'object' &&
            item.telemetry[valueKey] !== undefined
          ) {
            specificValue = item.telemetry[valueKey];
          } else {
            specificValue = item[valueKey];
          }

          if (
            typeof specificValue === 'number' &&
            !Number.isNaN(specificValue)
          ) {
            valueToAdd = specificValue;
          }
        } else {
          const checkForNumericValue = (obj: any, excludeKey: string) => {
            Object.keys(obj).forEach((key) => {
              if (
                key !== excludeKey &&
                typeof obj[key] === 'number' &&
                !Number.isNaN(obj[key])
              ) {
                valueToAdd = obj[key];
              }
            });
          };

          if (item.telemetry && typeof item.telemetry === 'object') {
            checkForNumericValue(item.telemetry, categoryKey);
          } else {
            checkForNumericValue(item, categoryKey);
          }
        }

        valueMap.set(category, currentValue + valueToAdd);
      }
    });

    return Array.from(valueMap.entries()).map(([name, value], index) => {
      const adxColors = [
        '#0078d4',
        '#107c10',
        '#ff4b4b',
        '#ffaa44',
        '#9b59b6',
        '#1abc9c',
        '#f39c12',
        '#e74c3c',
        '#3498db',
        '#2ecc71',
        '#e67e22',
        '#9013fe',
        '#00bcd4',
        '#ffc107',
        '#795548',
      ];

      const pieEntry = {
        name: String(name),
        value: Number(value),
        fill: adxColors[index % adxColors.length],
      };

      return pieEntry;
    });
  }, [
    chartType,
    chartData,
    widget.chartConfiguration.xAxis.columnName,
    widget.chartConfiguration.yAxis?.columnName,
  ]);

  const memoizedChart = useMemo(() => {
    const { xAxis, yAxis, dataSeries, showLegend } = widget.chartConfiguration;

    const commonTooltipStyle = {
      backgroundColor: 'white',
      border: '1px solid #2563eb',
      borderRadius: '8px',
      color: '#2563eb',
    };

    const renderChart = () => {
      switch (chartType) {
        case 'line':
          return (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey={xAxis.columnName || 'name'}
                stroke="#9ca3af"
                label={
                  xAxis.label
                    ? {
                      value: xAxis.label,
                      position: 'insideBottom',
                      offset: -10,
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <YAxis
                stroke="#9ca3af"
                label={
                  yAxis.label
                    ? {
                      value: yAxis.label,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <Tooltip contentStyle={commonTooltipStyle} />
              {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              {dataSeries && dataSeries.length > 0 ? (
                dataSeries.map((series, index) => (
                  <Line
                    key={series.columnName || index}
                    type="monotone"
                    dataKey={series.columnName}
                    stroke={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                    strokeWidth={2}
                    name={series.label || series.columnName}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey={yAxis.columnName || 'value'}
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name={yAxis.label || 'Value'}
                />
              )}
            </LineChart>
          );

        case 'bar':
          return (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey={xAxis.columnName || 'name'}
                stroke="#9ca3af"
                label={
                  xAxis.label
                    ? {
                      value: xAxis.label,
                      position: 'insideBottom',
                      offset: -10,
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <YAxis
                stroke="#9ca3af"
                label={
                  yAxis.label
                    ? {
                      value: yAxis.label,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <Tooltip contentStyle={commonTooltipStyle} />
              {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              {dataSeries && dataSeries.length > 0 ? (
                dataSeries.map((series, index) => (
                  <Bar
                    key={series.columnName || index}
                    dataKey={series.columnName}
                    fill={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                    name={series.label || series.columnName}
                  />
                ))
              ) : (
                <Bar
                  dataKey={yAxis.columnName || 'value'}
                  fill="#3b82f6"
                  name={yAxis.label || 'Value'}
                />
              )}
            </BarChart>
          );

        case 'area':
          return (
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey={xAxis.columnName || 'name'}
                stroke="#9ca3af"
                label={
                  xAxis.label
                    ? {
                      value: xAxis.label,
                      position: 'insideBottom',
                      offset: -10,
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <YAxis
                stroke="#9ca3af"
                label={
                  yAxis.label
                    ? {
                      value: yAxis.label,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <Tooltip contentStyle={commonTooltipStyle} />
              {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              {dataSeries && dataSeries.length > 0 ? (
                dataSeries.map((series, index) => (
                  <Area
                    key={series.columnName || index}
                    type="monotone"
                    dataKey={series.columnName}
                    stroke={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                    fill={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                    fillOpacity={0.3}
                    name={series.label || series.columnName}
                  />
                ))
              ) : (
                <Area
                  type="monotone"
                  dataKey={yAxis.columnName || 'value'}
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.3}
                  name={yAxis.label || 'Value'}
                />
              )}
            </AreaChart>
          );

        case 'pie':
          return (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ flex: 1, minHeight: 250 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={100}
                      dataKey="value"
                      nameKey="name"
                      fill="#8884d8"
                      // label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      labelLine={false}
                    >
                      {pieData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        fontSize: '14px',
                        color: '#495057',
                      }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length > 0) {
                          const data = payload[0].payload;
                          const total = pieData.reduce(
                            (sum, item) => sum + item.value,
                            0
                          );
                          const percentage = (
                            (data.value / total) *
                            100
                          ).toFixed(1);

                          return (
                            <div
                              style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '8px',
                                fontSize: '14px',
                                color: '#495057',
                              }}
                            >
                              {`${data.name}: ${data.value} (${percentage}%)`}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {/* {showLegend && (
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                    />
                  )} */}
                  </PieChart>
                </ResponsiveContainer>
              </div>
              {/* Category label at the bottom */}
              <div
                style={{
                  textAlign: 'center',
                  fontSize: '18px',
                  fontWeight: 500,
                  color: '#f87171',
                }}
              >
                {xAxis.label || xAxis.columnName || 'Category'}
              </div>
            </div>
          );

        case 'scatter':
          return (
            <ScatterChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey={xAxis.columnName || 'x'}
                stroke="#9ca3af"
                label={
                  xAxis.label
                    ? {
                      value: xAxis.label,
                      position: 'insideBottom',
                      offset: -10,
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <YAxis
                dataKey={yAxis.columnName || 'y'}
                stroke="#9ca3af"
                label={
                  yAxis.label
                    ? {
                      value: yAxis.label,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <Tooltip contentStyle={commonTooltipStyle} />
              {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              {dataSeries && dataSeries.length > 0 ? (
                dataSeries.map((series, index) => (
                  <Scatter
                    key={series.columnName || index}
                    dataKey={series.columnName}
                    fill={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                    name={series.label || series.columnName}
                  />
                ))
              ) : (
                <Scatter
                  dataKey={yAxis.columnName || 'value'}
                  fill="#3b82f6"
                  name={yAxis.label || 'Value'}
                />
              )}
            </ScatterChart>
          );

        case 'radar':
          return (
            <RadarChart data={chartData}>
              <PolarGrid stroke="#374151" />
              <PolarAngleAxis
                dataKey={xAxis.columnName || 'subject'}
                stroke="#9ca3af"
              />
              <PolarRadiusAxis stroke="#424750ff" />
              {dataSeries && dataSeries.length > 0 ? (
                dataSeries.map((series, index) => (
                  <Radar
                    key={series.columnName || index}
                    name={series.label || series.columnName}
                    dataKey={series.columnName}
                    stroke={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                    fill={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                    fillOpacity={0.3}
                  />
                ))
              ) : (
                <Radar
                  name={yAxis.label || 'Value'}
                  dataKey={yAxis.columnName || 'value'}
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.3}
                />
              )}
              <Tooltip contentStyle={commonTooltipStyle} />
              {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
            </RadarChart>
          );

        default:
          return (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey={xAxis.columnName || 'name'}
                stroke="#9ca3af"
                label={
                  xAxis.label
                    ? {
                      value: xAxis.label,
                      position: 'insideBottom',
                      offset: -10,
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <YAxis
                stroke="#9ca3af"
                label={
                  yAxis.label
                    ? {
                      value: yAxis.label,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }
                    : undefined
                }
              />
              <Tooltip contentStyle={commonTooltipStyle} />
              {showLegend && <Legend wrapperStyle={{ paddingTop: '20px' }} />}
              <Line
                type="monotone"
                dataKey={yAxis.columnName || 'value'}
                stroke="#3b82f6"
                strokeWidth={2}
                name={yAxis.label || 'Value'}
              />
            </LineChart>
          );
      }
    };

    return renderChart();
  }, [chartType, chartData, widget.chartConfiguration, pieData]);

  return (
    <Box sx={{ marginTop: '10px' }}>
      <WidgetEditControls
        widget={widget}
        onEdit={onEdit}
        onRemove={onRemove}
        onSave={onSave}
        hasUnsavedChanges={hasUnsavedChanges}
        isVisible={isEditing}
      />

      <Card
        sx={{
          height: '100%',
          bgcolor: 'white',
          border: '1px solid #d1d5db',
          color: 'black',
        }}
      >
        <CardHeader
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            '& .MuiCardHeader-title': { fontWeight: 600, fontSize: 18 },
          }}
          title={
            <Typography variant="h6" color="text.primary">
              {widget.title}
            </Typography>
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isLoading && (
                <CircularProgress size={20} sx={{ color: '#60a5fa', mr: 1 }} />
              )}
              <button
                onClick={() => refetch()}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <AutorenewIcon sx={{ fontSize: 20 }} />
              </button>
            </Box>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {(() => {
            if (error) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 128,
                    color: '#f87171',
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: 28, mr: 1 }} />
                  <span>Error loading data</span>
                </Box>
              );
            }

            if (!chartData || chartData.length === 0) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 128,
                    color: '#9ca3af',
                  }}
                >
                  <Typography variant="body2">
                    {isLoading
                      ? 'Loading data...'
                      : 'No data available. Configure the widget to fetch data.'}
                  </Typography>
                </Box>
              );
            }

            return (
              <Box sx={{ height: 256 }}>
                {chartType === 'pie' ? (
                  memoizedChart
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    {memoizedChart}
                  </ResponsiveContainer>
                )}
              </Box>
            );
          })()}
          <Typography
            variant="subtitle2"
            sx={{
              color: '#9ca3af',
              paddingTop: chartType === 'pie' ? '10px' : '1px',
            }}
          >
            Type: {widget.chartType} | Refresh: {widget.dataSource.refreshRate}s
            | Last updated:{' '}
            {data?.timestamp
              ? new Date(data.timestamp).toLocaleTimeString()
              : 'Never'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
